import cn from "classnames";

import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { StrokedText } from "@/components/ui/text/stroked-text";
import type { PokeTeam } from "@/types";
import { isXsScreen } from "@/utils/screen-size-check";

type TeamPokemon = PokeTeam["pokemons"][number];

interface DojoTeamSelectCardProps {
  team: PokeTeam;
  onClick?: (team: PokeTeam) => void;
}

function getOrderedSlots(team: PokeTeam): (TeamPokemon | null)[] {
  return Array.from({ length: 8 }).map((_, index) => {
    const pokeId = team.order?.[index as keyof typeof team.order];
    if (!pokeId) return null;
    const poke = team.pokemons.find((p) => p.id === pokeId);
    return poke ?? null;
  });
}

/**
 * Dojo team selection card - styled for training mode
 */
export function DojoTeamSelectCard({ team, onClick }: DojoTeamSelectCardProps) {
  const slots = getOrderedSlots(team);
  const filledCount = slots.filter((s) => s !== null).length;

  return (
    <div
      role={onClick ? "button" : undefined}
      onClick={onClick ? () => onClick(team) : undefined}
      className={cn(
        "group relative shadow-cartoon border-4 border-gray-900 rounded-xl overflow-hidden transition-all duration-200",
        onClick && "cursor-pointer hover:scale-[1.02] active:scale-[0.98]",
      )}
    >
      {/* Main card with gradient background */}
      <div className="relative bg-gradient-to-br from-pk-night-2 to-pk-night-1 p-3">
        {/* Animated shine sweep */}
        <div
          className={cn(
            "absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform -translate-x-full duration-700 ease-in-out pointer-events-none",
            onClick && "group-hover:translate-x-full",
          )}
        />

        {/* Texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage: "url('/assets/app-ui/textre.png')",
            backgroundSize: "cover",
            backgroundRepeat: "repeat",
          }}
        />

        {/* Skewed accent bar - red for dojo theme */}
        <div className="top-0 right-0 bottom-0 absolute bg-gradient-to-l from-pk-red-1/20 to-transparent w-1/3 skew-x-[-20deg] translate-x-8 pointer-events-none" />

        <div className="z-10 relative flex justify-between items-center mb-3">
          {/* Team name */}
          <div className="flex-1 min-w-0">
            <h3 className="drop-shadow-[2px_2px_0_rgba(0,0,0,0.8)] font-black text-white text-xl xs:text-2xl truncate italic uppercase tracking-wider">
              {team.name}
            </h3>
          </div>

          {/* Count badge */}
          <div className="flex items-center gap-1 ml-2">
            <div
              className={cn(
                "shadow-cartoon-sm px-2 py-0.5 rounded-sm font-bold text-xs uppercase tracking-wider",
                filledCount >= 8 ? "bg-pk-green-2 text-white" : "bg-pk-gray-3 text-pk-gray-5",
              )}
            >
              {filledCount}/8
            </div>
          </div>
        </div>

        {/* Pokemon grid */}
        <div className="z-10 relative gap-1 grid grid-cols-4">
          {slots.map((pokemon, index) => (
            <div
              key={pokemon ? pokemon.id : `empty-${index}`}
              className={cn(
                "relative flex justify-center items-center border-2 rounded-sm h-16 transition-all",
                pokemon
                  ? "bg-pk-red-1/30 border-pk-red-2 group-hover:bg-pk-red-1/50"
                  : "bg-pk-gray-1/30 border-pk-gray-2",
              )}
            >
              {pokemon ? (
                <>
                  {/* Sprite */}
                  <div className="top-1/2 left-1/2 absolute -translate-x-1/2 -translate-y-1/2">
                    <PokemonSprite
                      pokemon={pokemon}
                      className="w-14 h-14"
                      style={{ scale: isXsScreen() ? 1.3 : 1.6 }}
                      ignoreScale={true}
                    />
                  </div>

                  {/* Pokemon name at bottom */}
                  <div className="bottom-0 left-1/2 absolute px-0.5 w-full -translate-x-1/2">
                    <StrokedText fontSize={9} strokeWidth={6} className="text-center truncate">
                      {pokemon.base_pokemon_name}
                    </StrokedText>
                  </div>
                </>
              ) : (
                <span className="opacity-30 text-pk-gray-4 text-xl">—</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
